import { useEffect, useState } from "react";
import { useT } from "../i18n/context";
import type { ShownReaction } from "../store";
import { cardSide, cardStyle, type SeatSlot } from "./cardPlacement";
import { BUBBLE_MS, EMOJI } from "./social";

/**
 * A reaction by its pod (review 0022 decisions 141, 145): the sender's name and the words, or a big
 * emoji, towards the middle of the table for three seconds. Screen readers hear "Ayşe: Good game".
 */
export function ReactionBubble({
  shown,
  name,
  slot,
  gap,
}: {
  shown: ShownReaction;
  name: string;
  slot: SeatSlot;
  gap: number;
}) {
  const { t } = useT();
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    setVisible(true);
    const id = window.setTimeout(
      () => setVisible(false),
      Math.max(0, shown.at + BUBBLE_MS - Date.now()),
    );
    return () => window.clearTimeout(id);
  }, [shown]);
  if (!visible) return null;
  const emoji = EMOJI[shown.reaction];
  const text = t(`reaction.${shown.reaction}`);
  return (
    <span
      role="status"
      className="pointer-events-none absolute z-20 flex max-w-[180px] flex-col items-center rounded-[14px] border border-edge bg-panel-solid px-3 py-1.5 text-center shadow-panel motion-safe:animate-rise"
      style={cardStyle(cardSide(slot), gap)}
    >
      <span className="sr-only">{t("reaction.said", { name, text })}</span>
      <span aria-hidden="true" className="whitespace-nowrap text-[11px] font-bold text-ink/70">
        {name}
      </span>
      <span
        aria-hidden="true"
        className={
          emoji ? "text-[30px] leading-none" : "whitespace-nowrap text-[14px] font-extrabold"
        }
      >
        {emoji ?? text}
      </span>
    </span>
  );
}
